import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import i18n from "shell/i18n";

export default function PreviewMode(props) {
  const origin = window.location.origin;
  const preview = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const instance = useSelector((state) => state.instance);
  const content = useSelector((state) => state.content);
  const instanceSettings = useSelector((state) => state.settings.instance);
  const previewLock = useSelector((state) =>
    state.settings.instance.find(
      (setting) => setting.key === "preview_lock_password" && setting.value
    )
  );
  // Sends message to preview window to update route
  const route = useCallback(
    (itemZUID, version, dirty, hasErrors, model, extraPayload = {}) => {
      if (!preview.current?.contentWindow) return;
      // if not a string or a string that is not a content item zuid
      // then see if location contains a routable content item
      // only 7- resources are capable of having a path
      if (typeof itemZUID !== "string" || itemZUID.slice(0, 2) !== "7-") {
        itemZUID = location.pathname
          .split("/")
          .find((part) => part.slice(0, 2) === "7-");
      }
      if (!itemZUID) return;

      const item = content[itemZUID];
      const previewUrl = `${CONFIG.URL_MANAGER_PROTOCOL}${instance.ZUID}${CONFIG.URL_MANAGER}/active-preview`;

      let url = "";

      if (model?.type === "block") {
        url = `/-/block/${model.name}.html?variant=${item?.meta?.ZUID}&version=${version}&preview=global`;
      } else {
        url = item?.web?.path
          ? item.web.path
          : `/-/instant/${item?.meta?.ZUID}.json`;
        url += `?_bypassError=true&__version=${version}`;
      }

      if (previewLock) {
        url += `&zpw=${previewLock.value}`;
      }

      preview.current.contentWindow.postMessage(
        {
          source: "zesty",
          previewUrl,
          route: url,
          settings: instanceSettings,
          version,
          dirty,
          hasErrors,
          ...extraPayload,
        },
        origin
      );
    },
    [content, instance, instanceSettings, previewLock, origin]
  );

  // On iframe load, mark as loaded. The initial route + locale is deferred to
  // the "ready" handshake so we never send state before PreviewInner's listener
  // is registered.
  useEffect(() => {
    const iframe = preview?.current;
    if (!iframe) return;

    const onLoad = () => {
      setIsLoaded(true);
    };

    iframe.addEventListener("load", onLoad);

    // If iframe already loaded, trigger immediately
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc?.readyState === "complete") {
      onLoad();
    }

    return () => {
      iframe.removeEventListener("load", onLoad);
    };
  }, [preview?.current]);

  // On prop changes after initial load, push updated route to the iframe
  useEffect(() => {
    if (isLoaded) {
      route(
        props.itemZUID,
        props.version,
        props.dirty,
        props.hasErrors,
        props.model
      );
    }
  }, [
    props.itemZUID,
    props.version,
    props.dirty,
    props.hasErrors,
    props.model,
    isLoaded,
    route,
  ]);

  // Storing these as a ref to make sure we're always invoking the latest state
  // of these functions
  const onSaveRef = useRef(props.onSave);
  const onCloseRef = useRef(props.onClose);

  useEffect(() => {
    onSaveRef.current = props.onSave;
  }, [props.onSave]);

  useEffect(() => {
    onCloseRef.current = props.onClose;
  }, [props.onClose]);

  // Always-fresh snapshot of route() + current props so the "ready" handler
  // can send the full initial state without a stale closure.
  const callRouteRef = useRef(null);
  useEffect(() => {
    callRouteRef.current = (extraPayload = {}) =>
      route(
        props.itemZUID,
        props.version,
        props.dirty,
        props.hasErrors,
        props.model,
        extraPayload
      );
  });

  // Listen for postMessages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.source === "zesty") {
        switch (event.data.action) {
          case "ready":
            // Send the initial route and locale together in one message, now
            // that PreviewInner's listener is registered and ready to receive.
            callRouteRef.current?.({ locale: i18n.language });
            break;

          case "close":
            if (onCloseRef.current) {
              onCloseRef.current();
            }
            break;

          case "save":
            if (onSaveRef.current) {
              onSaveRef.current();
            }
            break;

          default:
            break;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [origin]);

  // Push locale changes to the iframe while it is open
  useEffect(() => {
    function onLanguageChanged(lang) {
      preview.current?.contentWindow?.postMessage(
        { source: "zesty", locale: lang },
        origin
      );
    }

    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, [origin]);

  return (
    <Box
      height="100%"
      width="100%"
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border}`,
        borderRadius: "8px",
      })}
      component="iframe"
      ref={preview}
      src={`${CONFIG.URL_MANAGER_PROTOCOL}${instance.ZUID}${CONFIG.URL_MANAGER}/active-preview`}
      frameBorder="0"
    />
  );
}
